import React, { createContext, useContext, useState, useEffect } from "react";
import {
  Slide,
  Button,
  Box,
  Typography,
  CircularProgress,
  Dialog,
  Checkbox,
  FormControlLabel,
  Divider,
  IconButton,
} from "@mui/material";
import { nudgeUser } from "../services/UserProfileApi"; // Import the API function
import { UserContext } from "../context";
import { Close } from "@mui/icons-material";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

// Create a Context
const NotificationbarContext = createContext();

// NotificationbarProvider Component
export function NotificationbarProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [notification, setNotification] = useState(null);
  const [dontRemind, setDontRemind] = useState(false);
  const { user, refreshUser } = useContext(UserContext);

  // Function to show different types of notifications
  const showNotificationbar = (type, options = {}) => {
    if (open) return; // Prevent multiple notifications
    setDontRemind(false); // Reset don't remind state for new notifications
    setNotification({ type, ...options });
    setOpen(true);
  };

  // Function to close the notification
  const handleClose = () => {
    setOpen(false);
    setTimeout(() => setNotification(null), 300); // Clear after animation
  };

  // Function for "Skip" in profile completion

  // const handleSkipProfile = () => {
  //   setNotification({
  //     type: "first_post",
  //     userData: user, // Ensure this field exists
  //     onPostNow: () => {
  //       setOpen(false);
  //     },
  //   });
  // };

  const handleSkipProfile = () => {
    setNotification({
      type: "first_post",
      userData: user, // Ensure this field exists
      onPostNow: () => {
        setOpen(false); // Close the notification
        setTimeout(() => {
          // Trigger the createPostDialog
          const feedComponent = document.getElementById("feed-component");
          if (feedComponent) {
            feedComponent.dispatchEvent(
              new CustomEvent("openCreatePostDialog")
            );
          }
        }, 200);
      },
    });
  };

  // Functions for first_post notification actions
  const handleDontRemind = (event) => {
    setDontRemind(event.target.checked);
  };

  const handlePostNow = () => {
    console.log("Post Now clicked", notification);
    if (notification?.onPostNow) {
      notification.onPostNow();
    }
    setOpen(false);
  };

  const handleSkip = async () => {
    if (dontRemind) {
      console.log("dontRemind is true, calling nudgeUser()");
      try {
        await nudgeUser();
        refreshUser();
      } catch (error) {
        console.error("Error in nudgeUser", error);
      }
      // nudgeUser(); // Call directly from here
    } else {
      console.log(
        "Skipped the notification. Next time it will show after 24 hours"
      );
      localStorage.setItem(
        "fc_last_post_notification_time",
        new Date().getTime()
      );
    }
    setOpen(false);
  };

  // Render different notifications based on type
  const renderNotificationContent = () => {
    if (!notification) return null;

    switch (notification.type) {
      case "profile_completion":
        return (
          <Box
            sx={{
              width: "100%",
              px: 1,
              py: 2,
              position: "relative",
              bgcolor: "background.paper",
            }}
          >
            {user?.no_of_posts === 0 ? (
              <Button
                variant="contained"
                size="small"
                disableElevation
                onClick={handleSkipProfile}
                sx={{
                  position: "absolute",
                  top: 10,
                  right: 10,
                  textTransform: "none",
                }}
              >
                Skip
              </Button>
            ) : (
              <IconButton
                onClick={handleClose}
                sx={{
                  position: "absolute",
                  top: 10,
                  right: 10,
                  color: "grey.500",
                }}
              >
                <Close />
              </IconButton>
            )}

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexDirection: "column",
              }}
            >
              <Box sx={{ position: "relative", display: "inline-flex" }}>
                <CircularProgress
                  size={60}
                  variant="determinate"
                  value={100}
                  sx={{ position: "absolute", color: "grey.300" }}
                />

                <CircularProgress
                  size={60}
                  variant="determinate"
                  value={notification.progress}
                  sx={{
                    " & .MuiCircularProgress-svg": {
                      transform: "rotate(-180deg) !important", // Correct rotation
                      transformOrigin: "center",
                    },
                  }}
                  color={
                    notification.progress > 75
                      ? "success"
                      : notification.progress > 50
                      ? "warning"
                      : "primary"
                  }
                />
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                  }}
                >
                  {notification.progress}%
                </Typography>
              </Box>

              <Box textAlign="center">
                <Typography variant="body1">
                  {notification.progress > 75
                    ? "You're Almost There! 🚀"
                    : notification.progress > 50
                    ? "Keep Up the Momentum! 🔥"
                    : "Let's Get Started! 💡"}
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {notification.progress > 75
                    ? "Just a few more steps to perfection!"
                    : notification.progress > 50
                    ? "You're making great progress—keep going!"
                    : "Build your profile and shine brighter!"}
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    justifyContent: "center",
                    mt: 1,
                  }}
                >
                  <Button
                    disableElevation
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={notification.onComplete}
                    sx={{ textTransform: "none" }}
                  >
                    Complete Profile
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        );

      case "first_post":
        return (
          <Box
            sx={{
              width: "100%",
              p: 2,
              pb: 1,
              position: "relative",
              bgcolor: "background.paper",
            }}
          >
            <Typography variant="body1" gutterBottom>
              Your Film Journey Starts Here!
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Whether you're making movies, acting, or just love film, your
              voice belongs here. Share your thoughts, experiences, or favorite
              moments with the community!
              <br /> <br />
              Hit “Write a Post” and join the conversation!
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Button
                variant="contained"
                color="primary"
                disableElevation
                onClick={handlePostNow}
                sx={{ textTransform: "none", mt: 2 }}
              >
                Write a Post
              </Button>
            </Box>
            <Divider sx={{ mt: 2 }} />
            <Box
              sx={{
                display: "flex",
                mt: 1,
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <FormControlLabel
                sx={{ "& .MuiFormControlLabel-label": { fontSize: 13 } }}
                control={
                  <Checkbox
                    onChange={handleDontRemind}
                    checked={dontRemind}
                    color="primary"
                    size="small"
                    sx={{
                      "& .MuiSvgIcon-root": { fontSize: 14 },
                    }}
                  />
                }
                label="Don't Remind Me"
              />

              <Button
                onClick={handleSkip}
                variant="text"
                size="small"
                color="inherit"
                sx={{
                  textTransform: "none",
                  px: 1.5,
                  py: 0.5,
                  "& .MuiButton-text": { fontSize: 10 },
                }}
              >
                Skip
              </Button>
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <NotificationbarContext.Provider value={{ showNotificationbar, setOpen }}>
      {children}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        aria-hidden={!open}
        TransitionComponent={Transition}
        aria-labelledby="dialog-title"
        aria-describedby="dialog-description"
        role="dialog"
      >
        {renderNotificationContent()}
      </Dialog>
    </NotificationbarContext.Provider>
  );
}

// Custom Hook to Use Notificationbar
export const useNotificationbar = () => useContext(NotificationbarContext);
